import React from 'react';
// eslint-disable-next-line no-restricted-imports
import { NavLink, NavLinkProps, NavLinkRenderProps, useLocation } from 'react-router-dom';
import cn from 'classnames';
import { Box, SxProps, Theme } from '@mui/material';
import { PACK } from 'web-shared/consts/packs';
import { parseUrlParams, stringifyUrlParams } from 'web-shared/utils/url-params';
import MuiLink from '@mui/material/Link';
import { getExternalLinkHref, getInternalLink, isInternalLink } from './utils';
import { useMemorizedLink } from './hooks/useMemorizedLink';

export interface LinkProps extends Omit<NavLinkProps, 'className'> {
  pack?: PACK;
  className?: string;
  testID?: string;
  sx?: SxProps<Theme>;
  innerRef?: React.Ref<HTMLElement>;
  isActive?: boolean;
  /**
   * if true, the current location will be memorized in the state
   */
  memorizeCurrentLocation?: boolean;
  /**
   * If true and the memorized location present in the state, it will be used instead of the default link
   */
  preferMemorizedLocation?: boolean;
  /**
   * If true and previously memorized location present in the state, it will be passed to the link as the state 
   */
  persistMemorizedLocation?: boolean;
}
interface LinkComponentProps {
  renderContent: (className?: string) => React.ReactElement;
}

const ForwardedLink = React.forwardRef<HTMLElement, LinkComponentProps & { className?: string }>(({ renderContent, ...props }, _ref) => {
  return renderContent(props.className);
});

const LinkComponent = React.forwardRef<HTMLElement, LinkComponentProps & { sx?: SxProps<Theme> }>(({ renderContent, sx }, ref) => {
  return (
    <MuiLink
      component={ForwardedLink}
      renderContent={renderContent}
      ref={ref}
      sx={sx}
    />
  );
});

const Link = ({
  pack,
  testID,
  className,
  memorizeCurrentLocation,
  preferMemorizedLocation,
  persistMemorizedLocation,
  innerRef,
  isActive,
  sx,
  ...props
}: LinkProps) => {
  // Parse the current location and extract the pack segment and any additional search parameters. 
  // This is used for memorizing and preferring the current location in external links.
  const location = useLocation();
  const [, prevLocationPack, ...restSearchParams] = parseUrlParams<{ prevLocation?: string }>(location.search).prevLocation?.split('/') ?? [];

  const resolvedPack = preferMemorizedLocation ? prevLocationPack as PACK ?? pack : pack;
  const memorization = useMemorizedLink();
  const internalLink = resolvedPack === undefined || isInternalLink(resolvedPack);

  const renderContent = (muiClassName?: string) => {
    const linkClassName = className ? cn(className, muiClassName) : muiClassName;

    const getNavLinkClassName = ({ isActive: routeIsActive }: NavLinkRenderProps) => cn(linkClassName, {
      active: isActive ?? routeIsActive,
    });

    const { to, style: __, ...rest } = props;

    if (internalLink) {

      if (persistMemorizedLocation) {
        const { to, state } = memorization.persistMemorizedLocation(props.to, props.state);
        return <NavLink {...rest} to={to} state={state} data-test-id={testID} className={getNavLinkClassName} />;
      }

      if (preferMemorizedLocation) {
        const { to, state } = memorization.getMemorizedLink(props.to, props.state);
        return <NavLink {...rest} to={to} state={state} data-test-id={testID} className={getNavLinkClassName} />;
      }

      const { to, state }: { to: NavLinkProps['to']; state?: any } = (() => {
        if (memorizeCurrentLocation) {
          return memorization.memorizeCurrentLocation(props.to, props.state);
        }
        return {
          to: getInternalLink(props.to),
          state: props.state,
        };
      })();

      return <NavLink {...rest} to={to} state={state} data-test-id={testID} className={getNavLinkClassName} />;
    }

    const { children, end: _, ...nativeLinkProps } = rest;

    let externalLink = getExternalLinkHref(resolvedPack, to);

    // Construct the external link URL, taking into account the previous pack segment and any memorized location.
    if (memorizeCurrentLocation) {
      const memorizedCurrentLocation = stringifyUrlParams({ prevLocation: `${window.location.pathname}${window.location.search || ''}` });
      externalLink = getExternalLinkHref(resolvedPack, to, memorizedCurrentLocation);
    }

    // Handle the case where the user prefers the memorized location for the external link.
    if (preferMemorizedLocation) {
      const to = `/${restSearchParams.join('/')}`;
      externalLink = getExternalLinkHref(resolvedPack, to);
    }

    return (
      <Box
        component="a"
        data-test-id={testID}
        href={externalLink}
        className={linkClassName}
        {...nativeLinkProps}
      >
        {children as React.ReactNode}
      </Box>
    );
  };

  return <LinkComponent renderContent={renderContent} sx={sx} ref={innerRef} />;

};

export default Link;